import { ScreenSpaceEventType, Cartesian3, AxisAlignedBoundingBox, Color, Cartographic, } from "cesium";
import { CesiumPointSelectorType, } from "../../cesium-utils/point-selector/point-selector";
import { EarthBrainUtils } from "../../utilities/earthbrainutility";
export class Drawing2DConstructionHandler {
    _callback;
    _viewer;
    _preview;
    _handler;
    _cesiumPointSelector;
    _site;
    _pickedPositionsWGS84;
    _pickedPointsEntity;
    _onKeyPressHandler;
    _corners;
    _isUnderConstruction;
    constructor(args, cesiumViewer) {
        this._site = args.site;
        this._viewer = args.viewer;
        this._preview = args.preview;
        this._handler = args.handler;
        this._cesiumPointSelector =
            args.cesiumPointSelectorFactory.getPointSelector(CesiumPointSelectorType.Default);
        this._pickedPositionsWGS84 = [];
        this._pickedPointsEntity = [];
        this._isUnderConstruction = false;
    }
    _mouseMoveDebounce = undefined;
    _debounceHandlerForSphereMovement(movement) {
        if (this._mouseMoveDebounce) {
            clearTimeout(this._mouseMoveDebounce);
            this._mouseMoveDebounce = undefined;
        }
        this._mouseMoveDebounce = setTimeout(() => {
            this._functionForPreviewSphereMovement(movement);
            this._mouseMoveDebounce = undefined;
        }, 100);
    }
    async _functionForPreviewSphereMovement(movement) {
        const cartesian = await this._cesiumPointSelector.getPointOnMousePos(movement.endPosition);
        if (cartesian) {
            this._preview.setSpherePosition(cartesian);
            if (this._pickedPositionsWGS84.length > 0) {
                const localPositions = await this._site.convertPointsToLocalSpace([
                    ...this._pickedPositionsWGS84,
                    cartesian,
                ]);
                const aabb = AxisAlignedBoundingBox.fromPoints(localPositions);
                const localCorners = [
                    new Cartesian3(aabb.minimum.x, aabb.minimum.y, aabb.maximum.z),
                    new Cartesian3(aabb.maximum.x, aabb.minimum.y, aabb.maximum.z),
                    new Cartesian3(aabb.maximum.x, aabb.maximum.y, aabb.maximum.z),
                    new Cartesian3(aabb.minimum.x, aabb.maximum.y, aabb.maximum.z),
                ];
                const wgs84Corners = await this._site.convertPointsToWGS84(localCorners);
                this._preview.setLinePosition([...wgs84Corners, wgs84Corners[0]]);
            }
            this._viewer.scene.requestRender();
        }
    }
    async addPointOnLeftClick({ position: mousePos, }) {
        const cartesian = await this._cesiumPointSelector.getPointOnMousePos(mousePos);
        if (cartesian) {
            this._pickedPositionsWGS84.push(cartesian);
            const entity = EarthBrainUtils.addSphere(this._viewer, cartesian, Color.PINK, Number.POSITIVE_INFINITY, 5);
            this._pickedPointsEntity.push(entity);
            this._viewer.scene.requestRender();
            if (this._pickedPositionsWGS84.length > 1) {
                const localPositions = await this._site.convertPointsToLocalSpace(this._pickedPositionsWGS84);
                const aabb = AxisAlignedBoundingBox.fromPoints(localPositions);
                this._corners.local = [
                    new Cartesian3(aabb.minimum.x, aabb.minimum.y, aabb.maximum.z),
                    new Cartesian3(aabb.maximum.x, aabb.minimum.y, aabb.maximum.z),
                    new Cartesian3(aabb.maximum.x, aabb.maximum.y, aabb.maximum.z),
                    new Cartesian3(aabb.minimum.x, aabb.maximum.y, aabb.maximum.z),
                ];
                this._corners.wgs84 = await this._site.convertPointsToWGS84(this._corners.local);
                this._preview.setLinePosition([
                    ...this._corners.wgs84,
                    this._corners.wgs84[0],
                ]);
            }
        }
    }
    async onKeyPressed(event) {
        switch (event.key) {
            case "Esc":
            case "Escape":
                this.cancel();
                break;
            case "Enter":
                this.end();
                break;
            default:
        }
    }
    start(callback) {
        this._callback = callback;
        this._isUnderConstruction = true;
        this._corners = { local: [], wgs84: [] };
        this._preview.setSphereVisibility(true);
        this._preview.setLineVisibility(true);
        this._pickedPositionsWGS84.length = 0;
        this._pickedPointsEntity.length = 0;
        this._handler.setInputAction(this._debounceHandlerForSphereMovement.bind(this), ScreenSpaceEventType.MOUSE_MOVE);
        this._onKeyPressHandler = this.onKeyPressed.bind(this);
        this._handler.setInputAction(this.addPointOnLeftClick.bind(this), ScreenSpaceEventType.LEFT_CLICK);
        document.addEventListener("keydown", this._onKeyPressHandler);
        this._callback.onStart && this._callback.onStart();
    }
    _removeInputActions() {
        this._handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this._handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
        document.removeEventListener("keydown", this._onKeyPressHandler);
    }
    _clearAllRendered() {
        this._pickedPointsEntity.forEach((entity) => this._viewer.entities.remove(entity));
        this._pickedPointsEntity.length = 0;
        this._preview.setSphereVisibility(false);
        this._preview.setLineVisibility(false);
        this._preview.setSpherePosition(undefined);
        this._preview.setLinePosition([]);
    }
    end() {
        this._isUnderConstruction = false;
        this._removeInputActions();
        this._clearAllRendered();
        this._pickedPositionsWGS84.length = 0;
        const elevations = this._corners.wgs84.map((corner) => Cartographic.fromCartesian(corner).height);
        if (this._callback.onFinish) {
            if (this._corners.wgs84.length === 4) {
                const data = {
                    cornerPositions: [
                        this._corners.wgs84[3],
                        this._corners.wgs84[2],
                        this._corners.wgs84[0],
                        this._corners.wgs84[1],
                    ],
                    elevation: Math.max(...elevations),
                };
                this._callback.onFinish(data);
            }
            else {
                this._callback.onFinish(undefined);
            }
        }
    }
    cancel() {
        this._isUnderConstruction = false;
        this._removeInputActions();
        this._clearAllRendered();
        this._corners.local.length = 0;
        this._corners.wgs84.length = 0;
        this._pickedPositionsWGS84.length = 0;
        this._callback.onCancel && this._callback.onCancel();
    }
    isUnderConstruction() {
        return this._isUnderConstruction;
    }
}
//# sourceMappingURL=drawing-2d-construction-handler.js.map